/**
 * Auth shell for /login, /register, /activate. A single full-screen
 * background image (branding.authBackgroundUrl) with the sign-in form
 * positioned by the broker-picked variant:
 *   - image-right  → form docked right  (image fills the left)
 *   - image-left   → form docked left   (image fills the right)
 *   - image-center → form centered on top of the full image
 *
 * The variant comes from `tenant.settings.authLayout`. On mobile the form
 * always centers; the left/right docking applies from `lg:` up. Falls back
 * to a brand gradient when no background image is uploaded.
 */
import { Outlet, Link } from "react-router-dom";
import { useCurrentTenant } from "@/hooks/useTenant";
import type { AuthLayoutVariant } from "@/types/tenantSettings";
import { cn } from "@/lib/utils";
import { Brand } from "./Brand";

export function AuthLayout() {
  const { data: tenantInfo } = useCurrentTenant();
  const variant: AuthLayoutVariant =
    tenantInfo?.tenant?.settings?.authLayout ?? "image-right";
  const tagline = tenantInfo?.tenant?.settings?.branding?.tagline;
  const heroUrl = tenantInfo?.tenant?.settings?.branding?.authBackgroundUrl;

  // Horizontal placement of the form (mobile always centers).
  const lgPos =
    variant === "image-left" ? "lg:justify-start"
    : variant === "image-center" ? "lg:justify-center"
    : "lg:justify-end";

  // Show the background image clean — no dimming overlay. The form is a solid
  // card, so it stays readable without hazing the image.
  const bg: React.CSSProperties | undefined = heroUrl
    ? {
        backgroundImage: `url(${heroUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  return (
    <div className="relative min-h-screen bg-bg overflow-hidden" style={bg}>
      {/* Brand-gradient fallback when no background image is uploaded. */}
      {!heroUrl && (
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
          <div className="absolute inset-0 bg-grad-primary opacity-[0.12]" />
          <div className="absolute -top-40 -left-24 h-[34rem] w-[34rem] rounded-full blur-3xl opacity-30 bg-grad-primary" />
          <div className="absolute -bottom-40 -right-24 h-[36rem] w-[36rem] rounded-full blur-3xl opacity-25 bg-grad-accent" />
        </div>
      )}

      <div className={cn("relative min-h-screen flex items-center justify-center p-4 sm:p-8 lg:p-16", lgPos)}>
        <div className="w-full max-w-lg">
          <div className="rounded-2xl bg-bg-elevated/90 backdrop-blur-xl border border-border/60 p-8 sm:p-10 lg:p-12 shadow-2xl">
            <div className="flex flex-col items-center text-center mb-8">
              <Brand size="lg" />
              {tagline && <p className="text-sm text-fg-muted mt-3 max-w-xs">{tagline}</p>}
            </div>
            <Outlet />
          </div>
          <p className="text-center text-xs text-fg-subtle mt-4">
            © 2026 RDFX · <Link to="/" className="hover:text-fg">rdfxglobal.com</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
